import { NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromLayout from '../../../layout';
import { NEW_CHAT_TITLE_DEFAULT } from '../../chats.constants';
import { ChatsHelper } from '../../chats.helper';
import { ChatNavigationItem } from '../../chats.interfaces';
import { RouterService } from '../../shared/services/router.service';
import { ChatsActions, chatsFeature, ChatsState } from '../../store';
import {
  CHAT_TITLE_MAX_LENGTH,
  CHAT_TITLE_PATTERN,
  getChatRenameCommit,
} from './navigation-chats-list.helper';

@Component({
  selector: 'squadbox-navigation-chats-list-smart',
  imports: [
    TranslateModule,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    NgClass,
  ],
  template: `
    <div class="flex flex-1 flex-col gap-1">
      <div class="navigation-label">
        {{ label() }}
      </div>
      @if (!chatNavigationItems().length) {
        <div class="navigation-empty-state">
          <svg
            sdUseNavigationIcon="new-chat"
            class="size-5 shrink-0 text-primary-300"
          ></svg>
          <span class="typo-caption">{{
            'MAIN_NAVIGATION_ITEMS.NO_CHATS' | translate
          }}</span>
        </div>
      } @else {
        @for (chat of chatNavigationItems(); track chat.id) {
          @if (editingChatId() === chat.id) {
            <div
              class="
                navigation-item
                h-10! rounded! bg-primary-50! p-2!
              "
              [style.view-transition-name]="
                chat.id === renameInvokerId() ? 'chat-rename-row' : null
              "
            >
              <input
                #renameInput
                type="text"
                [value]="editingInitialTitle()"
                [attr.maxlength]="CHAT_TITLE_MAX_LENGTH"
                [attr.pattern]="CHAT_TITLE_PATTERN.source"
                [attr.aria-label]="
                  'CHAT.RENAME_CHAT_MODAL.PLACEHOLDER' | translate
                "
                class="
                  typo-p3
                  h-8 w-full min-w-0
                  rounded-lg border border-neutral-200
                  bg-white px-4 py-2
                  cursor-text select-text
                  text-neutral-700 caret-primary-500
                  focus:border-primary-500 focus:outline-hidden
                "
                (keydown.enter)="commitRename($event, chat, renameInput.value)"
                (keydown.escape)="cancelRenameAndRestoreFocus($event)"
                (blur)="handleRenameBlur(chat, renameInput.value)"
              />
            </div>
          } @else {
            <div
              #chatRow
              role="button"
              tabindex="0"
              [attr.data-chat-id]="chat.id"
              class="navigation-item group tablet-portrait:h-10!"
              [style.view-transition-name]="
                chat.id === renameInvokerId() ? 'chat-rename-row' : null
              "
              [ngClass]="{
                selected: selectedChatId() === chat.id,
              }"
              (click)="handleClickChatCTA(chat)"
              (keydown.enter)="handleClickChatCTA(chat)"
              (keydown.space)="
                handleClickChatCTA(chat); $event.preventDefault()
              "
            >
              <span class="truncate">{{
                chat.title === NEW_CHAT_TITLE_DEFAULT
                  ? (chat.title | translate)
                  : chat.title
              }}</span>
              @if (!isMobile()) {
                <!-- a11y note: the dropdown trigger lives inside an interactive
                      row (outer div is role="button"). Nesting role="button" is
                      invalid ARIA; splitting the row into sibling focusable
                      controls is deferred work (SB-3479). Until then the
                      trigger stays keyboard-operable itself: focusable,
                      visible on focus, Enter/Space/ArrowDown open the menu.
                      The outer row's keydown handlers still open the chat. -->
                <svg
                  sdUseUIBasicIcon="more-horizontal"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="
                    'CHAT.DROPDOWN_MENU.TRIGGER_LABEL' | translate
                  "
                  class="
                    size-6 shrink-0
                    aria-expanded:text-primary-500 aria-expanded:opacity-100
                    text-neutral-700
                    group-hover:ml-2 aria-expanded:ml-2 focus-visible:ml-2
                    focus:outline-hidden
                    focus-visible:outline-2 focus-visible:outline-primary-500
                    opacity-0 group-hover:opacity-100 focus-visible:opacity-100
                  "
                  [sdDropdownMenuTrigger]="chatDropdown"
                  (click)="handleClickChatDropdownCTA($event, chat)"
                  (keydown.enter)="armChatDropdown($event, chat)"
                  (keydown.space)="armChatDropdown($event, chat)"
                  (keydown.arrowDown)="armChatDropdown($event, chat)"
                ></svg>
              }
            </div>
          }
        }
      }
      <!-- Chat Dropdown Menu -->
      <sd-dropdown-menu
        #chatDropdown
        [options]="chatDropdownMenuOptions()"
        [responsive]="false"
      ></sd-dropdown-menu>
    </div>
  `,
})
export class NavigationChatsListSmartComponent {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  protected readonly chatsHelper = inject(ChatsHelper);
  private readonly routerService = inject(RouterService);
  private readonly renameInput =
    viewChild<ElementRef<HTMLInputElement>>('renameInput');
  private readonly chatRows = viewChildren<ElementRef<HTMLElement>>('chatRow');
  protected readonly renameInvokerId = signal<number | null>(null);

  public readonly label = input.required<string>();
  public readonly chatNavigationItems = input.required<ChatNavigationItem[]>();
  public readonly chatDeleteClicked = output<number>();
  public readonly chatClicked = output<void>();

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly selectedChatId = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChatId,
  );
  public readonly chatDropdownActiveChat = signal<ChatNavigationItem | null>(
    null,
  );
  public readonly editingChatId = signal<number | null>(null);
  /**
   * Seeded once on entering edit mode: a live store binding would wipe the
   * user's in-progress typing whenever the title changes underneath (e.g. a
   * socket auto-title landing mid-edit).
   */
  protected readonly editingInitialTitle = signal('');
  public readonly NEW_CHAT_TITLE_DEFAULT = NEW_CHAT_TITLE_DEFAULT;
  protected readonly CHAT_TITLE_MAX_LENGTH = CHAT_TITLE_MAX_LENGTH;
  protected readonly CHAT_TITLE_PATTERN = CHAT_TITLE_PATTERN;

  public readonly chatDropdownMenuOptions = computed<
    fromSeriousSystem.DropdownMenuOption[]
  >(() => {
    return this.chatsHelper.getChatDropdownMenuOptions({
      delete: () => {
        untracked(() => {
          const chatIdToDelete = this.chatDropdownActiveChat()?.id;
          if (chatIdToDelete) {
            this.chatDeleteClicked.emit(chatIdToDelete);
          }
        });
      },
      rename: () => {
        untracked(() => {
          const chat = this.chatDropdownActiveChat();
          if (chat) {
            this.enterEditMode(chat);
          }
        });
      },
    });
  });

  public async handleClickChatCTA(chat: ChatNavigationItem) {
    if (this.editingChatId() === chat.id) {
      return;
    }

    // Navigate to conversation with chat ID and assistant UUID if present
    if (chat.assistantId) {
      await this.routerService.navigateToConversationWithChatAndAssistant(
        chat.id,
        chat.assistantId,
      );
    } else {
      await this.routerService.navigateToConversationWithChat(chat.id);
    }
    this.chatClicked.emit();
  }

  public handleClickChatDropdownCTA(
    event: MouseEvent,
    chat: ChatNavigationItem,
  ) {
    event.stopPropagation();
    this.chatDropdownActiveChat.set(chat);
  }

  public armChatDropdown(event: Event, chat: ChatNavigationItem) {
    // CdkMenuTrigger on the same element opens the menu for Enter/Space/
    // ArrowDown (it also preventDefaults Space); this handler only arms the
    // target chat and keeps the key from bubbling into the row's
    // open-the-chat handlers.
    event.stopPropagation();
    this.chatDropdownActiveChat.set(chat);
  }

  public enterEditMode(chat: ChatNavigationItem) {
    this.renameInvokerId.set(chat.id);
    this.editingInitialTitle.set(this.chatsHelper.getChatTitle(chat.title));
    this.changeDetectorRef.detectChanges();
    this.updateEditingChatId(chat.id, () => {
      const input = this.renameInput()?.nativeElement;
      input?.focus();
      // The prefilled title opens fully selected (type-to-replace).
      input?.select();
    });
  }

  public commitRename(
    event: Event,
    chat: ChatNavigationItem,
    rawValue: string,
  ) {
    event.preventDefault();
    event.stopPropagation();
    this.commitRenameValue(chat, rawValue);
  }

  public cancelRenameAndRestoreFocus(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.exitEditModeAndRestoreFocus();
  }

  public handleRenameBlur(chat: ChatNavigationItem, rawValue: string) {
    window.setTimeout(() => {
      if (this.editingChatId() === chat.id) {
        this.commitRenameValue(chat, rawValue);
      }
    });
  }

  private commitRenameValue(chat: ChatNavigationItem, rawValue: string) {
    const rename = getChatRenameCommit(
      chat,
      this.chatsHelper.getChatTitle(chat.title),
      rawValue,
    );

    if (rename) {
      this.chatsStore.dispatch(
        ChatsActions.renameChat({
          id: chat.id,
          title: rename.title,
          previousTitle: rename.previousTitle,
          requestId: crypto.randomUUID(),
          renamedAt: new Date().toISOString(),
          source: 'desktop-inline',
        }),
      );
    }

    this.exitEditModeAndRestoreFocus();
  }

  private exitEditModeAndRestoreFocus() {
    if (this.editingChatId() === null) {
      return;
    }

    this.updateEditingChatId(null, () => {
      const renameInvokerId = this.renameInvokerId();
      const row = this.chatRows().find(
        (chatRow) =>
          chatRow.nativeElement.dataset['chatId'] === `${renameInvokerId}`,
      );

      row?.nativeElement.focus();
    });
  }

  private updateEditingChatId(
    chatId: number | null,
    onUpdateComplete?: () => void,
  ) {
    const update = () => {
      this.editingChatId.set(chatId);
      this.changeDetectorRef.detectChanges();
    };
    const viewTransitionDocument =
      typeof document === 'undefined' ? undefined : document;
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (viewTransitionDocument?.startViewTransition && !prefersReducedMotion) {
      const viewTransition = viewTransitionDocument.startViewTransition(update);
      if (onUpdateComplete) {
        void viewTransition.updateCallbackDone.then(onUpdateComplete);
      }
      return;
    }

    update();
    onUpdateComplete?.();
  }
}
